import { Routes, UrlMatcher, UrlSegment } from '@angular/router';
import {
    canMatchDigitalProductPublicProduct,
    canMatchSubscriptionPublicProduct
} from './user/guards/public-product-type.guard';

const publicPageWithIdentifierMatcher: UrlMatcher = (segments: UrlSegment[]) => {
    if (segments.length !== 2) {
        return null;
    }

    const [loginSegment, identifierSegment] = segments;
    if (!loginSegment.path.startsWith('@') || loginSegment.path.length <= 1 || !identifierSegment.path) {
        return null;
    }

    return {
        consumed: [loginSegment, identifierSegment],
        posParams: {
            userLogin: new UrlSegment(loginSegment.path.slice(1), {}),
            id: identifierSegment
        }
    };
};

const publicPageLegacyMatcher: UrlMatcher = (segments: UrlSegment[]) => {
    if (segments.length !== 1) {
        return null;
    }

    const [loginSegment] = segments;
    if (!loginSegment.path.startsWith('@') || loginSegment.path.length <= 1) {
        return null;
    }

    return {
        consumed: [loginSegment],
        posParams: {
            userLogin: new UrlSegment(loginSegment.path.slice(1), {})
        }
    };
};

export const routes: Routes = [
    {
        path: 'user/register',
        loadComponent: () =>
            import('./user/components/register/register.component')
                .then(m => m.UserRegisterComponent)
    },
    {
        path: 'user/login',
        loadComponent: () =>
            import('./user/components/login/login.component')
                .then(m => m.default)
    },
    {
        path: 'user/logout',
        loadComponent: () =>
            import('./user/components/logout/logout.component')
                .then(m => m.UserLogoutComponent)
    },
    {
        path: 'user/profile',
        loadComponent: () =>
            import('./user-profile/components/profile/profile.component')
                .then(m => m.UserProfileComponent)
    },
    {
        path: 'user/purchases',
        loadComponent: () =>
            import('./order/components/purchase-list/purchaseList.component')
                .then(m => m.PurchaseListComponent)
    },
    {
        path: 'user/timeout',
        loadComponent: () =>
            import('./user/components/access-timeout/access-timeout.component')
                .then(m => m.default)
    },
    {
        path: 'user/activate/instructions',
        loadComponent: () =>
            import('./user/components/user-activation-instructions/user-activation-instructions.component')
                .then(m => m.UserActivationInstructionsComponent)
    },
    {
        path: 'user/activate/success',
        loadComponent: () =>
            import('./user/components/user-activation-success/user-activation-success.component')
                .then(m => m.UserActivationSuccessComponent)
    },
    {
        path: 'user/activate/error',
        loadComponent: () =>
            import('./user/components/user-activation-error/user-activation-error.component')
                .then(m => m.UserActivationErrorComponent)
    },
    {
        path: 'user/activate/:token',
        loadComponent: () =>
            import('./user/components/user-activation/user-activation.component')
                .then(m => m.UserActivationComponent)
    },
    {
        path: 'user/waitingList',
        loadComponent: () =>
            import('./user/components/waiting-list/waiting-list.component')
                .then(m => m.WaitingListComponent)
    },
    {
        path: 'user/billing',
        loadComponent: () =>
            import('./order/components/order-list/orderList.component')
                .then(m => m.OrderListComponent)
    },
    {
        path: 'user/reset-password/:token',
        loadComponent: () =>
            import('./user/components/user-reset-password/reset-password.component')
                .then(m => m.ResetPasswordComponent)
    },
    {
        path: 'mypage',
        loadComponent: () =>
            import('./user/components/myPage/mypage.component')
                .then(m => m.MyPageComponent)
    },
    {
        path: 'messages',
        loadComponent: () =>
            import('./messages/components/message-main/message-main.component')
                .then(m => m.MessageMainComponent)
    },
    {
        path: 'createPost',
        loadComponent: () =>
            import('./posts/components/postDetails/postDetails.component')
                .then(m => m.PostDetailsComponent)
    },
    {
        path: 'editPost/:id',
        loadComponent: () =>
            import('./posts/components/postDetails/postDetails.component')
                .then(m => m.PostDetailsComponent)
    },
    {
        path: 'post/:id',
        loadComponent: () =>
            import('./posts/components/post-view/post-view.component')
                .then(m => m.PostViewComponent)
    },
    {
        path: 'contact',
        loadComponent: () =>
            import('./contacts/components/contacts/contacts.component')
                .then(m => m.default)
    },
    {
        path: 'manageConnections',
        loadComponent: () =>
            import('./connections/components/manage/manage-connections.component')
                .then(m => m.ManageConnectionsComponent)
    },
    {
        path: 'manageCommunities',
        loadComponent: () =>
            import('./communities/components/manage/manage-communities.component')
                .then(m => m.ManageCommunitiesComponent)
    },
    {
        path: 'groups',
        loadComponent: () =>
            import('./groups/components/manage/manage-groups.component')
                .then(m => m.ManageGroupsComponent)
    },
    {
        path: 'groups/create',
        loadComponent: () =>
            import('./groups/components/upsert/upsert-groups.component')
                .then(m => m.UpsertGroupsComponent)
    },
    {
        path: 'groups/create/:id',
        loadComponent: () =>
            import('./groups/components/upsert/upsert-groups.component')
                .then(m => m.UpsertGroupsComponent)
    },
    {
        path: 'groups/discussion/:id',
        loadComponent: () =>
            import('./groups/components/discussion/group-discussion.component')
                .then(m => m.GroupDiscussionComponent)
    },
    {
        path: 'community/:id',
        loadComponent: () =>
            import('./communities/components/community-content/community-content.component')
                .then(m => m.CommunityContentComponent)
    },
    {
        path: 'community/create',
        loadComponent: () =>
            import('./communities/components/upsert/upsert-communities.component')
                .then(m => m.UpsertCommunitiesComponent)
    },
    {
        path: 'community/create/:id',
        loadComponent: () =>
            import('./communities/components/upsert/upsert-communities.component')
                .then(m => m.UpsertCommunitiesComponent)
    },
    {
        path: 'course/enrolled',
        loadComponent: () =>
            import('./courses/components/course-enrolled/courseEnrolled.component')
                .then(m => m.CourseEnrolledComponent)
    },
    {
        path: 'course/mycourses',
        pathMatch: 'full',
        loadComponent: () =>
            import('./courses/components/my-courses/myCourses.component')
                .then(m => m.MyCoursesComponent)
    },
    {
        path: 'course/learn/:id',
        pathMatch: 'full',
        loadComponent: () =>
            import('./courses/components/course-learn-index/courseLearnIndex.component')
                .then(m => m.CourseLearnIndexComponent)
    },
    {
        path: 'course/:id',
        pathMatch: 'full',
        loadComponent: () =>
            import('./courses/components/course-details/course-details.component')
                .then(m => m.CourseDetailsComponent)
    },
    {
        path: 'lesson',
        pathMatch: 'full',
        loadComponent: () =>
            import('./courses/components/lesson-learn/lesson.component')
                .then(m => m.LessonComponent)
    },
    {
        path: 'shoppingCart',
        loadComponent: () =>
            import('./shoppingCart/components/shoppingCart.component')
                .then(m => m.ShoppingCartComponent)
    },
    {
        path: 'checkout/subscription-success',
        loadComponent: () =>
            import('./checkout/components/subscription-success/subscription-success.component')
                .then(m => m.SubscriptionSuccessComponent)
    },
    {
        path: 'login-sso',
        loadComponent: () =>
            import('./user/components/login-sso/login-sso.component')
                .then(m => m.LoginSsoComponent)
    },
    {
        path: 'about',
        loadComponent: () =>
            import('./core/components/about/about.component')
                .then(m => m.AboutComponent)
    },
    {
        path: 'pricing',
        loadComponent: () =>
            import('./core/components/pricing/pricing.component')
                .then(m => m.PricingComponent)
    },
    {
        matcher: publicPageWithIdentifierMatcher,
        canMatch: [canMatchSubscriptionPublicProduct],
        loadComponent: () =>
            import('./user/components/subscription-public-page/subscription-public-page.component')
                .then(m => m.SubscriptionPublicPageComponent)
    },
    {
        matcher: publicPageWithIdentifierMatcher,
        canMatch: [canMatchDigitalProductPublicProduct],
        loadComponent: () =>
            import('./user/components/digital-product-public-page/digital-product-public-page.component')
                .then(m => m.DigitalProductPublicPageComponent)
    },
    {
        matcher: publicPageLegacyMatcher,
        loadComponent: () =>
            import('./user/components/myPublicPage/my-public-page.component')
                .then(m => m.MyPublicPageComponent)
    },
    {
        path: '',
        loadComponent: () =>
            import('./core/components/mainpage/mainpage.component')
                .then(m => m.MainPageComponent)
    },
    {
        path: '**',
        pathMatch: 'full',
        loadComponent: () =>
            import('./core/components/pageNotFound/pageNotFound.component')
                .then(m => m.PageNotFoundComponent)
    }
];
